import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import {
    Dialog,
    DialogContent,
    makeStyles,
    RadioGroup,
    Theme,
    Typography
} from '@material-ui/core'
import React from 'react'
import ManageUnmappedDialogActions from './DialogActions'
import ManageUnmappedDialogTitle from './DialogTitle'
import RadioButton from './RadioButton'

const useStyles = makeStyles((theme: Theme) => ({
    dialog: {
        '& .MuiDialog-paper': {
            width: pxToRem(500),
        },
    },
    dialogContent: {
        padding: theme.spacing(3, 3, 2),
    },
    description: {
        fontSize: pxToRem(16),
        color: '#4D4D4D',
        marginBottom: theme.spacing(2),
        lineHeight: 1.5,
    },
    questionText: {
        fontSize: pxToRem(16),
        color: '#4D4D4D',
        marginBottom: theme.spacing(2),
    },
    radioGroup: {
        marginLeft: theme.spacing(1),
    },

}))

/**
 * Props for the ManageUnmappedProductDialog component
 */
export interface ManageUnmappedProductDialogProps {
    /** Whether the dialog is open */
    open: boolean
    /** Callback when the dialog should be closed */
    onClose: () => void
    /** Callback when user selects an option and continues */
    onSaveAndContinue: (selectedOption: string) => void
    /** Optional name of the product being managed */
    productName?: string
}

/**
 * Initial step dialog for managing unmapped products.
 * 
 * Presents the user with two options:
 * 1. Add the product to an existing MasterSpec family
 * 2. Create a new family for the product
 * 
 * @param props - The component props
 * @returns The ManageUnmappedProductDialog component
 */
const ManageUnmappedProductDialog: React.FC<ManageUnmappedProductDialogProps> = ({
    open,
    onClose,
    onSaveAndContinue,
    productName,
}) => {
    const classes = useStyles()
    const [selectedOption, setSelectedOption] = React.useState<string>('existing')

    const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSelectedOption(event.target.value)
    }

    const handleSaveAndContinue = () => {
        if (selectedOption) {
            onSaveAndContinue(selectedOption)
        }
    }

    const handleClose = () => {
        setSelectedOption('existing')
        onClose()
    }

    return (
        <Dialog
            open={open}
            onClose={(_, reason) => {
                if (reason !== 'backdropClick') {
                    handleClose()
                }
            }}
            className={classes.dialog}
            aria-labelledby="manage-unmapped-dialog-title"
        >
            <ManageUnmappedDialogTitle>
                Manage Unmapped Product
            </ManageUnmappedDialogTitle>
            <DialogContent className={classes.dialogContent}>
                <Typography className={classes.description}>
                    This product cannot be added to a project until it is
                    included in a Family.
                </Typography>
                <Typography className={classes.questionText}>
                    How do you want to proceed?
                </Typography>
                <RadioGroup
                    value={selectedOption}
                    onChange={handleRadioChange}
                    className={classes.radioGroup}
                >
                    <RadioButton
                        value="existing"
                        label="Add this product listing to an existing MasterSpec family"

                    />
                    <RadioButton
                        value="new"
                        label="Create a new family for this product listing"
                    />
                </RadioGroup>
            </DialogContent>
            <ManageUnmappedDialogActions
                onCancel={handleClose}
                onContinue={handleSaveAndContinue}
                disabled={!selectedOption}
            />
        </Dialog>
    )
}

export default ManageUnmappedProductDialog